import {ServiceAndStatus} from 'src/dtos/service-with-status.dto';
import {
    ActionOption,
    Address,
    AddressGroup,
    Domain,
    FwVersion,
    FwVersionInfo,
    Rule,
    Service,
    UpdatedRule,
    UrlCategory
} from 'src/dtos/version.dto';
import {AddressIPType, AddressType, PolicyType, VersionState, VersionStatus} from './enums';
import {SystemConfiguration} from './system-configuration';
import {RuleHelper, RuleNameAlias} from './rule-helper';

type VersionType = FwVersion | FwVersionInfo | undefined;

export const DRAFT_STATUSES = [VersionStatus.DRAFT, VersionStatus.COMMIT_ERROR, VersionStatus.PUSH_ERROR, VersionStatus.COMMIT_OK];

export const PENDING_STATUSES = [VersionStatus.COMMIT_SENT, VersionStatus.PUSH_SENT];

export function isVersionPending(version: VersionType): boolean {
    return isVersionStatusPending(version?.versionStatus);
}

export function isVersionStatusPending(versionStatus: VersionStatus | undefined): boolean {
    return !!versionStatus && PENDING_STATUSES.includes(versionStatus);
}

export function isVersionDraft(version: VersionType): boolean {
    const status = version?.versionStatus;
    return !!status && DRAFT_STATUSES.includes(status);
}

export function isVersionActive(version: VersionType): boolean {
    return version?.versionState === VersionState.ACTIVE && version.versionStatus === VersionStatus.PUSH_OK;
}
export function isVersionInactive(version: VersionType): boolean {
    return version?.versionState === VersionState.INACTIVE && version.versionStatus === VersionStatus.PUSH_OK;
}

export function isVersionSmart(fwVersion: FwVersionInfo | undefined): boolean {
    return fwVersion?.versionType === PolicyType.SMART_POLICY;
}

export const isValidatedOrInactive = (fwVersion: FwVersionInfo | undefined) =>
    fwVersion && (fwVersion.versionStatus === VersionStatus.COMMIT_OK || fwVersion.versionStatus === VersionStatus.PUSH_OK)
    && fwVersion.versionState === VersionState.INACTIVE;

export function getBlockCategoriesListByVersion(version: FwVersion | undefined) {
    const categories = version?.content?.policy?.url_filtering?.[0]?.categories || [];
    return categories.filter(category => category.action === 'block').map(category => category.name);
}

export function getAllBronzeCategoriesListByVersion(version: FwVersion | undefined) {
    const categories = version?.content?.policy?.url_filtering?.[0]?.categories || [];
    return categories.map(category => ({name: category.name, action: category.action}));
}

export function getManagedDomainsCountByVersion(version: FwVersion | undefined) {
    return version?.content?.policy.url_categories?.reduce((count, urlCategory) => count + urlCategory.domainsDetails.length, 0);
}

export function mapDomains(categories: UrlCategory[] | undefined): Domain[] {
    return categories?.flatMap(urlCategory => urlCategory.domainsDetails) || [];
}

export function getAddressGroupsByVersion(version: FwVersion | undefined) {
    return version?.content.policy.address_groups || [];
}

export function getRulesByVersionAndTypeOptional(version: FwVersion | undefined, actionType?: ActionOption) {
    const rules = version?.content.policy.rules || [];
    if (actionType)
        return rules.filter(_ => _.action === actionType);
    return rules;
}

export function getDefaultRulesByVersionAndTypeOptional(version: FwVersion | undefined, actionType?: ActionOption) {
    const rules = version?.content.policy.rules || [];
    const srcRuleName = RuleHelper.getRuleName(RuleNameAlias.ALLOW_UNTRUST_SRC);
    const destRuleName = RuleHelper.getRuleName(RuleNameAlias.ALLOW_UNTRUST_DEST);

    if (actionType)
        return rules.filter(_ => (_.displayName !== srcRuleName && _.displayName !== destRuleName)  &&_.action === actionType && _.isDefault);
    return rules;
}

export function getRuleById(version: FwVersion | undefined, ruleId: string): Rule | undefined {
    const rules = version?.content.policy.rules || [];
    if (!ruleId) return undefined;
    return rules.find(_ => _.id === ruleId);
}

export function getAddressGroupsByVersionAndAddress(version: FwVersion | undefined, address: Address): AddressGroup[] {
    return getAddressGroupsByVersion(version)
        .filter(group => group.addresses.includes(address.id));
}

export function mapAddresses(addresses: Address[] | undefined, addressGroups: AddressGroup[] | undefined): Address[] {
    return addresses?.map(_ => {
        return {
            ..._,
            ipType: getTypeByValue(_.value),
            associatedAddressGroupList: addressGroups
                ?.filter(group => group.addresses.includes(_.id))
                .map(_ => _.displayName) || []
        };
    }) || [];
}

export function isAddressHasRule(rule: Rule, address: Address, addressGroups: AddressGroup[]) {
    const relevantGroups = new Set(addressGroups
        .filter(group => group.addresses.includes(address.id))
        .map(group => group.id));

    const srcRules = rule.src.address.address_groups.filter(group => relevantGroups.has(group));
    const dstRules = rule.dst.address.address_groups.filter(group => relevantGroups.has(group));

    return srcRules.length > 0 || dstRules.length > 0;
}

export function isAddressGroupHasRule(rule: Rule, addressGroup: AddressGroup) {

    const srcRules = rule.src.address.address_groups.filter(group => addressGroup.id === group);
    const dstRules = rule.dst.address.address_groups.filter(group => addressGroup.id === group);

    return srcRules.length > 0 || dstRules.length > 0;
}

export function isAddressGroupContainIpType(addressGroup: AddressGroup, addresses: Address[], ipVersion: number) : boolean {
    return addresses.filter(adr => addressGroup.addresses.includes(adr.value))
        .some(adr => adr.ipVersion === ipVersion);
}

export function isServiceHasRule(rule: Rule, service: Service) {
    return rule.service.services?.filter(_ => _.includes(service.id)).length > 0;

}

export function getAddressType(type: AddressIPType): AddressType {
    if ([AddressIPType.IP_NETMASK, AddressIPType.IP_SUBNET].includes(type)) {
        return AddressType.IPV4_NETMASK;
    }
    return AddressType.IPV4_RANGE;
}

export function buildAddressPayload(address: Address) {
    const { associatedAddressGroupList,
        displayName,
        description } = address;
    return {
        addressKeyDto: {
            type: address.type,
            value: address.value
        },
        associatedAddressGroupList,
        displayName,
        description
    };
}
export function isNotSaneAddress(address: string): boolean {
    return SystemConfiguration.getInstance().saneDoNoDeleteAddressName !== address;
}
export function addressesInGroup(addressGroup: AddressGroup, filterSaneDoNoDelete = true) {
    if (filterSaneDoNoDelete) {
        return addressGroup.addresses?.filter(address => isNotSaneAddress(address));
    }
    return addressGroup.addresses;
}

export function getPolicyRuleByDisplayName(displayName: string, version: FwVersion) {
    return version?.content.policy.rules.find(rule => rule.displayName === displayName);
}

export function getTypeByValue(value: string) {
    if (value.includes('-')) {
        return AddressIPType.IP_RANGE;
    } else if (value.includes('/')) {
        return AddressIPType.IP_SUBNET;
    }
    return AddressIPType.IP_NETMASK;
}

export function isServiceInactive(rule: Rule, service: ServiceAndStatus): boolean {
    if (rule.disabled) {
        return rule.service.services.includes(service.id) || rule.application.applications.includes(service.id);
    }
    return false;
}

export function getServiceInactiveDirection(pageRules: (Rule | null)[],
    inBoundRules: (Rule | null)[],
    outBoundRules: (Rule | null)[],
    data: ServiceAndStatus[],
    isAllRuleDisable: boolean,
    isAllRuleActive: boolean): ServiceAndStatus[] {
    data.forEach(service => {
        if (isAllRuleDisable || isAllRuleActive) {
            service.isInboundInactive = false;
            service.isOutboundInactive = false;
        } else {
            service.isInboundInactive = pageRules.some(rule => !!rule && isServiceInactive(rule, service) && inBoundRules.includes(rule));
            service.isOutboundInactive = pageRules.some(rule => !!rule && isServiceInactive(rule, service) && outBoundRules.includes(rule));
        }
    });
    return data;
}

export function convertServiceAndStatusToCustomServiceType(defaultService: ServiceAndStatus): Service {
    return {
        id: defaultService.id,
        displayName: defaultService.name,
        description: defaultService.serviceDescription.toString(),
        proto: defaultService.protocol,
        sports: defaultService.sourcePort ? defaultService.sourcePort.replace(/\s+/g, '').split(',') : '',
        dports: defaultService.destinationPort ? defaultService.destinationPort.replace(/\s+/g, '').split(',') : '',
        serviceStateOutbound: defaultService.outboundState,
        serviceStateInbound: defaultService.inboundState,
        isDefault: !defaultService.isCustom
    } as Service;
}

export const buildUpdateRuleRequest: (updatedRule: Rule, originalRule: Rule) => UpdatedRule = (updatedRule: Rule, originalRule: Rule) => {
    return {
        ...updatedRule,
        originalDisplayName: originalRule.displayName,
        newPosition: updatedRule.position
    };
};
